import { isPresent, omit, throwErrorUnlessProduction } from '@luminovo/commons';
import { Autocomplete, Paper, styled } from '@mui/material';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';
import * as React from 'react';

import { colorSystem } from '../../../theme';
import { TertiaryButton } from '../../buttons';
import { Checkbox } from '../../Checkbox';
import { Chip } from '../../Chip';
import { Divider } from '../../Divider';
import { Flexbox } from '../../Flexbox';
import { Highlight } from '../../Highlight';
import { Text } from '../../Text';
import { FieldText } from '../FieldText';
import { FieldMultiSelectProps } from './types';
import { generateVirtualizeProps, VirualizedGroupContainer } from './VirtualizeComponents';

/** On blur, autoSelect can pass `undefined` from a stale highlight, so both sides are guarded. */
export function areOptionsEqualByKey<TValue>(
    getOptionKey: (value: TValue) => string,
    option: TValue | null | undefined,
    value: TValue | null | undefined,
): boolean {
    return isPresent(option) && isPresent(value) && getOptionKey(option) === getOptionKey(value);
}

const GroupHeader = styled('div')({
    position: 'sticky',
    top: '-8px',
    backgroundColor: colorSystem.neutral.white,
    zIndex: 1,
});

const GroupItems = styled('ul')({
    padding: 0,
});

function FieldMultiSelectInner<TValue>(
    {
        value,
        options,
        virtualized = false,
        onChange,
        onAfterChange,
        getOptionKey,
        getOptionLabel,
        getOptionSublabel,
        getOptionBadge,
        getOptionColor = () => 'neutral',
        renderOption,
        placeholder,
        error,
        helperText,
        action,
        renderGroupLabel,
        ...rest
    }: FieldMultiSelectProps<TValue>,
    outerRef: React.ForwardedRef<unknown>,
): JSX.Element {
    assertUniqueOptionLables({ options, getOptionLabel, getOptionKey });
    const [userTyped, setUserTyped] = React.useState(false);

    const virtualizeSlots = virtualized ? generateVirtualizeProps() : undefined;

    return (
        <Autocomplete
            multiple={true}
            autoSelect={true}
            ref={outerRef}
            // TODO: Our type system doesn't detect when TValue is `null`, we need to fix this.
            value={value ?? []}
            options={options}
            onChange={(event, value) => {
                // A blur from disableCloseOnSelect would change the selection unintentionally.
                if (event.type === 'blur') {
                    /* no-op */
                } else {
                    onChange(value);
                    onAfterChange?.(value);
                }
            }}
            getOptionLabel={getOptionLabel}
            onInputChange={(_, inputValue, reason) => {
                setUserTyped(reason === 'input'); // Only set to true when user types
            }}
            isOptionEqualToValue={(option, value) => areOptionsEqualByKey(getOptionKey, option, value)}
            renderValue={(value, getItemProps) =>
                value.map((item, index) => {
                    const { key, ...tagProps } = getItemProps({ index });
                    return (
                        <Chip
                            key={key}
                            label={getOptionLabel(item)}
                            color={getOptionColor(item)}
                            style={{
                                marginLeft: '4px',
                                marginRight: '4px',
                                marginTop: '2px',
                                marginBottom: '2px',
                            }}
                            {...tagProps}
                        />
                    );
                })
            }
            renderOption={
                isPresent(renderOption)
                    ? ({ key, ...props }, option, state) => (
                          <li key={key} {...props}>
                              {renderOption(option, state)}
                          </li>
                      )
                    : ({ key, ...props }, option, { inputValue, selected }) => {
                          const label = getOptionLabel?.(option) ?? JSON.stringify(option);
                          const subLabel = getOptionSublabel?.(option);
                          const badge = getOptionBadge?.(option);

                          return (
                              <li key={key} {...props}>
                                  <Flexbox
                                      sx={{
                                          justifyContent: 'space-between',
                                          width: '100%',
                                          marginLeft: isPresent(renderGroupLabel) ? undefined : '-8px',
                                      }}
                                  >
                                      <Flexbox
                                          key={getOptionKey(option)}
                                          gap={8}
                                          sx={{
                                              alignItems: 'top',
                                          }}
                                      >
                                          <Checkbox checked={selected} size={'small'} />
                                          <Flexbox
                                              gap={2}
                                              sx={{
                                                  flexDirection: 'column',
                                                  width: '100%',
                                              }}
                                          >
                                              {isPresent(label) && (
                                                  <Highlight
                                                      label={label}
                                                      matcher={userTyped ? [inputValue] : []}
                                                      overrides={{
                                                          Container: HighLightLabelContainer,
                                                      }}
                                                  />
                                              )}

                                              {isPresent(subLabel) && (
                                                  <Highlight
                                                      label={subLabel}
                                                      matcher={userTyped ? [inputValue] : []}
                                                      overrides={{
                                                          Container: HighLightSubLabelContainer,
                                                      }}
                                                  />
                                              )}
                                          </Flexbox>
                                      </Flexbox>
                                      {badge}
                                  </Flexbox>
                              </li>
                          );
                      }
            }
            renderInput={(props) => (
                // Hack: `value` and `onChange` are required but unused: `slotProps.htmlInput.value` overrides them.
                <FieldText
                    {...props}
                    type="text"
                    placeholder={!isPresent(value) || value.length === 0 ? placeholder : undefined}
                    value={null}
                    onChange={() => {}}
                    error={error}
                    helperText={helperText}
                />
            )}
            renderGroup={
                isPresent(renderGroupLabel) && virtualized
                    ? (params) => (
                          <VirualizedGroupContainer innerChildren={params.children} label={renderGroupLabel(params)} />
                      )
                    : (params) => (
                          <li key={params.key}>
                              <GroupHeader>
                                  <Text variant="h5" color={colorSystem.neutral[8]}>
                                      {params.group}
                                  </Text>
                              </GroupHeader>
                              <GroupItems>{params.children}</GroupItems>
                          </li>
                      )
            }
            slots={{
                paper: action
                    ? (props) => (
                          <Paper {...props} style={{ position: 'relative' }}>
                              {props.children}
                              <Divider />
                              <Flexbox
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                      justifyContent: 'space-between',
                                      width: '100%',
                                      padding: '4px',
                                  }}
                              >
                                  <TertiaryButton
                                      size="small"
                                      startIcon={<PlusIcon />}
                                      onClick={action?.onClick}
                                      disabled={action?.disabled}
                                  >
                                      {action?.label}
                                  </TertiaryButton>
                              </Flexbox>
                          </Paper>
                      )
                    : undefined,
                ...virtualizeSlots,
            }}
            autoHighlight={true}
            popupIcon={<ChevronDownIcon size={24} />}
            {...omit(rest, 'inputRef')}
        />
    );
}

const HighLightLabelContainer = (props: React.PropsWithChildren<{}>) => (
    <Text variant={'body-small'} color={colorSystem.neutral[9]}>
        {props.children}
    </Text>
);
const HighLightSubLabelContainer = (props: React.PropsWithChildren<{}>) => (
    <Text variant={'caption'} color={colorSystem.neutral[6]} showEllipsis>
        {props.children}
    </Text>
);

/** Option keys, or labels without `getOptionKey`, must be unique: they're the options' React keys. */
function assertUniqueOptionLables<TValue>({
    options,
    getOptionLabel,
    getOptionKey,
}: {
    options: TValue[];
    getOptionLabel?: (option: TValue) => string;
    getOptionKey?: (option: TValue) => string | number;
}): void {
    const keySet = new Set<string | number>();
    const labelSet = new Set<string>();

    options.forEach((option) => {
        if (isPresent(getOptionKey)) {
            const key = getOptionKey(option);
            if (keySet.has(key)) {
                throwErrorUnlessProduction(`FieldSelect: Duplicate keys found in options. Keys must be unique`, {
                    extra: { key },
                });
            } else {
                keySet.add(key);
            }
        }

        if (!isPresent(getOptionKey) && isPresent(getOptionLabel)) {
            const label = getOptionLabel(option);
            if (labelSet.has(label)) {
                throwErrorUnlessProduction(
                    'FieldSelect: Duplicate lable found in options. Label must be unique or provide a getOptionKey function',
                    { extra: { label } },
                );
            } else {
                labelSet.add(label);
            }
        }
    });
}

export const FieldMultiSelect = React.forwardRef(FieldMultiSelectInner) as <TValue>(
    props: FieldMultiSelectProps<TValue> & { ref?: React.ForwardedRef<unknown> },
) => JSX.Element;
